/**
 * Profile Page Loader
 * Pre-loads user profile data before rendering
 */

import type { LoaderFunctionArgs } from 'react-router-dom';
import { requireAuth, loaderCache } from './helpers';
import { mockUser, mockProfileStats } from '@/modules/profile/pages/data';
import {
  trackRouteLoad,
  trackRouteError,
  startRouteTimer,
  calculateDataSize,
} from '@/utils/route-analytics';
import { getProfileAPI, getStoredUser } from '@/services/keycloakAuthService';
import { useAuthStore } from '@/stores/authStore';

export interface ProfileData {
  user: {
    id: string;
    firstName: string;
    lastName: string;
    email: string;
    phoneNumber: string;
    avatar?: string;
    accountType: string;
    organization?: {
      name: string;
      role: string;
    };
  };
  preferences: {
    notifications: {
      email: boolean;
      sms: boolean;
      push: boolean;
    };
    privacy: {
      profileVisibility: string;
      dataSharing: boolean;
    };
    display: {
      theme: string;
      language: string;
      timezone: string;
    };
  };
  security: {
    twoFactorEnabled: boolean;
    lastPasswordChange: string;
    activeSessions: number;
  };
  stats: {
    memberSince: string;
    totalOrders: number;
    totalSpent: number;
    averageRating: number;
  };
}

/**
 * Fetch profile data from API, falling back to SecureStorage/mock data
 */
async function fetchProfileData(): Promise<ProfileData> {
  // Check cache first
  const cached = loaderCache.get<ProfileData>('profile');
  if (cached) {
    return cached;
  }

  try {
    // Fetch from real API
    const response = await getProfileAPI();
    const apiUser = response.data;

    const data: ProfileData = {
      user: {
        id: apiUser.id,
        firstName: apiUser.first_name || '',
        lastName: apiUser.last_name || '',
        email: apiUser.email,
        phoneNumber: apiUser.phone || '',
        avatar: apiUser.avatar_url || undefined,
        accountType: apiUser.user_type || 'individual',
        organization: apiUser.organization_name
          ? { name: apiUser.organization_name, role: 'owner' }
          : undefined,
      },
      preferences: {
        notifications: { email: true, sms: false, push: true },
        privacy: { profileVisibility: 'private', dataSharing: false },
        display: { theme: 'light', language: 'en', timezone: 'UTC' },
      },
      security: {
        twoFactorEnabled: apiUser.two_factor_enabled || false,
        lastPasswordChange: apiUser.updated_at || new Date().toISOString(),
        activeSessions: 1,
      },
      stats: mockProfileStats as unknown as ProfileData['stats'],
    };

    // Sync to Zustand store so Navigation header stays current
    useAuthStore.getState().updateUserProfile({
      first_name: apiUser.first_name,
      last_name: apiUser.last_name,
      full_name: `${apiUser.first_name || ''} ${apiUser.last_name || ''}`.trim(),
      email: apiUser.email,
      phone: apiUser.phone,
      avatar_url: apiUser.avatar_url,
      organization_name: apiUser.organization_name,
    });

    // Cache for 5 minutes
    loaderCache.set('profile', data, 5 * 60 * 1000);
    return data;
  } catch (_error) {
    // Fall back to SecureStorage data if API fails
    const storedUser = await getStoredUser();

    const data: ProfileData = {
      user: storedUser
        ? ({
            id: storedUser.id || mockUser.id,
            firstName: storedUser.first_name ?? mockUser.firstName,
            lastName: storedUser.last_name ?? mockUser.lastName,
            email: storedUser.email ?? mockUser.email,
            phoneNumber: storedUser.phone ?? mockUser.phone ?? '',
            avatar: (storedUser as { avatar_url?: string }).avatar_url ?? mockUser.avatar,
            accountType: (storedUser as { user_type?: string }).user_type ?? mockUser.accountType,
            organization: (storedUser as { organization_name?: string }).organization_name
              ? {
                  name: (storedUser as { organization_name?: string }).organization_name!,
                  role: 'owner',
                }
              : undefined,
          } as ProfileData['user'])
        : (mockUser as unknown as ProfileData['user']),
      preferences: {
        notifications: { email: true, sms: false, push: true },
        privacy: { profileVisibility: 'private', dataSharing: false },
        display: { theme: 'light', language: 'en', timezone: 'UTC' },
      },
      security: {
        twoFactorEnabled: false,
        lastPasswordChange: new Date().toISOString(),
        activeSessions: 1,
      },
      stats: mockProfileStats as unknown as ProfileData['stats'],
    };

    // Cache for 5 minutes
    loaderCache.set('profile', data, 5 * 60 * 1000);
    return data;
  }
}

/**
 * Profile loader function
 */
export async function profileLoader(args: LoaderFunctionArgs) {
  const getElapsed = startRouteTimer();

  try {
    // Check authentication
    await requireAuth(args.request);

    // Fetch profile data
    const data = await fetchProfileData();

    // Track performance
    const loadTime = getElapsed();
    const dataSize = calculateDataSize(data);
    trackRouteLoad('/profile', loadTime, dataSize);

    // Return data directly (React Router loaders don't need to be wrapped)
    return data;
  } catch (error) {
    // If it's a redirect Response, let it propagate
    if (error instanceof Response) {
      throw error;
    }

    trackRouteError('/profile', error as Error);
    throw error;
  }
}
